import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button,
  Table,
  message,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Input,
  Modal,
  Form,
  Popconfirm,
  Empty,
  Spin,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import "../../css/sm-art-common.css";

import CriticalOperationService from "../../services/CriticalOperationService";

const { Title, Text } = Typography;

const CriticalMachineDevice = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CriticalOperationService.listCriticalDevices();
      console.log("Device API:", res);

      let rows = [];

      if (res?.ok && res?.data) {
        if (Array.isArray(res.data.data)) {
          rows = res.data.data;
        } else if (Array.isArray(res.data.devices)) {
          rows = res.data.devices;
        } else if (Array.isArray(res.data.result)) {
          rows = res.data.result;
        } else if (Array.isArray(res.data)) {
          rows = res.data;
        }
      }

      setDevices(rows);
    } catch (error) {
      console.error(error);
      message.error("Failed to load devices");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleAdd = () => {
    setEditingRow(null);
    form.resetFields();
    setOpenModal(true);
  };

  const handleEdit = (record) => {
    setEditingRow(record);
    form.setFieldsValue({
      device_id: record.device_id,
    });
    setOpenModal(true);
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      let res;

      if (editingRow) {
        res = await CriticalOperationService.updateCriticalDevice(
          editingRow.d_id,
          values.device_id
        );
      } else {
        res = await CriticalOperationService.addCriticalDevice(values.device_id);
      }

      if (res?.ok && (res?.data?.status || res?.data?.success)) {
        message.success(
          editingRow ? "Device updated successfully" : "Device added successfully"
        );
        setOpenModal(false);
        setEditingRow(null);
        form.resetFields();
        loadDevices();
      } else {
        message.error(res?.data?.message || "Failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await CriticalOperationService.deleteCriticalDevice(record.d_id);

      if (res?.ok && (res?.data?.status || res?.data?.success)) {
        message.success("Device deleted");
        loadDevices();
      } else {
        message.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Delete failed");
    }
  };

  const filteredDevices = useMemo(() => {
    if (!Array.isArray(devices)) return [];

    return devices.filter((d) => {
      const q = searchText.toLowerCase();
      return (
        String(d?.device_id || "").toLowerCase().includes(q) ||
        String(d?.d_id || "").toLowerCase().includes(q) ||
        String(d?.added_on || "").toLowerCase().includes(q)
      );
    });
  }, [devices, searchText]);

  const columns = [
    {
      title: "ID",
      dataIndex: "d_id",
      width: 100,
      align: "center",
    },
    {
      title: "Device ID",
      dataIndex: "device_id",
      align: "left",
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      align: "center",
      render: (value) => value || "-",
    },
    {
      title: "Actions",
      width: 150,
      align: "center",
      render: (_, record) => (
        <div className="sm-action-group">
          <Button
            className="sm-icon-btn sm-icon-btn-edit"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />

          <Popconfirm
            title="Delete device?"
            description="Are you sure you want to delete this device?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              className="sm-icon-btn sm-icon-btn-delete"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const closeModal = () => {
    setOpenModal(false);
    setEditingRow(null);
    form.resetFields();
  };

  return (
    <div className="sm-page">
      <div className="sm-page-inner">
        <div className="sm-header">
          <div className="sm-title-wrap">
            <Title level={3} className="sm-page-title">
              <DesktopOutlined style={{ color: "#2150FD" }} />
              Critical Machine Devices
            </Title>
            <Text className="sm-page-subtitle">
              Manage all critical machine devices used in operations.
            </Text>
          </div>

          <div className="sm-top-actions">
            <Button className="sm-btn-soft" icon={<ReloadOutlined />} onClick={loadDevices}>
              Refresh
            </Button>

            <Button
              className="sm-btn-primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add New Device
            </Button>
          </div>
        </div>

        <Card className="sm-card">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={10} lg={8}>
              <label className="sm-label">Search Device</label>
              <Input
                className="sm-input"
                placeholder="Search device..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
          </Row>
        </Card>

        <Card className="sm-card" style={{ marginTop: 16 }}>
          <Spin spinning={loading}>
            {!filteredDevices.length ? (
              <div className="sm-empty-wrap">
                <Empty description={<span className="sm-empty-text">No data found.</span>} />
              </div>
            ) : (
              <div className="sm-table-wrap">
                <Table
                  className="sm-table"
                  dataSource={filteredDevices}
                  columns={columns}
                  bordered
                  size="middle"
                  rowKey={(record, index) => record.d_id || record.device_id || index}
                  scroll={{ x: 800 }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                    position: ["bottomCenter"],
                    className: "sm-pagination",
                  }}
                />
              </div>
            )}
          </Spin>
        </Card>

        <Modal
          open={openModal}
          title={editingRow ? "Edit Device" : "Add New Device"}
          onCancel={closeModal}
          footer={null}
          centered
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={handleSave}>
            <Form.Item
              label="Device ID"
              name="device_id"
              rules={[{ required: true, message: "Device ID required" }]}
            >
              <Input className="sm-input" placeholder="Enter Device ID" />
            </Form.Item>

         <div className="sm-modal-footer">
  <Button className="sm-btn-cancel" onClick={closeModal}>
    Cancel
  </Button>
  <Button
    className="sm-btn-primary"
    htmlType="submit"
    loading={saving}
  >
    {editingRow ? "Update Device" : "Save Device"}
  </Button>
</div>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default CriticalMachineDevice;